import React, { Component, ReactElement, ReactNode } from 'react'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import compose from 'lodash/flowRight'
import omit from 'lodash/omit'
import styled from 'styled-components'

import { AlertDialog } from '~/views/components/ui/Dialog'
import DrawerWithPanel from '~/views/components/ui/Drawer/DrawerWithPanel'
import Text from '~/views/components/ui/Text'
import { withRouter, WithRouter } from '~/views/hooks/useRouter'
import FormDrawerErrorBoundary from './FormDrawerErrorBoundary'

interface Props extends WithRouter {
  children: ReactNode
  onClose(): void
  searchParam: string
  title: string
  unsavedFiles: boolean
  warningOnClose?: boolean
  promptUpload: boolean
  setPromptUpload(promptUpload: boolean): void
  setRemoveUnsavedFiles?: (removeUnsavedFiles: boolean) => void
  editTaskUrlState?: string
  subtitle?: string
  imageUrl?: string
  customHeader?: ReactElement
  useEventTasksV2SaveChangesDialog: false
  eventTasksV2FormValues: Record<string, any>
  description?: string
}

interface State {
  openSaveChangesConfirmDialog: boolean
}
const StyledText = styled(Text)`
  margin: auto;
  justify-content: center;
  display: flex;
`
const DialogText = styled.span`
  display: flex;
  text-align: center;
`
class FormDrawer extends Component<Props, State> {
  override state = {
    openSaveChangesConfirmDialog: false,
  }

  onCloseDrawer = () => {
    const { onClose, warningOnClose } = this.props

    if (warningOnClose) {
      this.openDialog()
    } else {
      onClose && onClose()
    }
  }

  closeDrawer = () => {
    this.closeDialog()
    this.props.onClose && this.props.onClose()
  }

  closeDialog = (shouldUpload = true) => {
    if (this.props.promptUpload && shouldUpload)
      this.props.setPromptUpload(false)
    this.setState({ openSaveChangesConfirmDialog: false })
  }

  openDialog = () => {
    this.setState({ openSaveChangesConfirmDialog: true })
  }

  changeTab = () => {
    const { router, editTaskUrlState, setPromptUpload, setRemoveUnsavedFiles } =
      this.props

    if (editTaskUrlState) {
      router.push(editTaskUrlState, undefined, { shallow: true })
    }

    setPromptUpload(false)

    setRemoveUnsavedFiles?.(true)
  }

  renderDrawer = (props: Props) => {
    const {
      children: propChildren,
      searchParam,
      title,
      subtitle,
      imageUrl,
      customHeader,
      unsavedFiles,
      promptUpload,
      router,
      description,
      ...rest
    } = this.props
    const open = router?.query[searchParam] === 'true'

    const children = React.Children.map(propChildren, (child) => {
      return React.cloneElement(
        child as React.DetailedReactHTMLElement<any, any>,
        { ...props },
      )
    })

    return (
      <DrawerWithPanel
        {...omit(rest, ['warningOnClose', 'onSubmit'])}
        anchor="right"
        open={open}
        onClose={this.onCloseDrawer}
        title={title}
        subtitle={subtitle}
        description={description}
        imageUrl={imageUrl ? imageUrl : ''}
        customHeader={customHeader}
      >
        {open && (
          <FormDrawerErrorBoundary onClose={this.closeDrawer}>
            {children}
          </FormDrawerErrorBoundary>
        )}

        <ConditionalDialog
          {...this}
          unsavedFiles={unsavedFiles}
          promptUpload={promptUpload}
          openSaveChangesConfirmDialog={this.state.openSaveChangesConfirmDialog}
          open={open}
        >
          {children}
        </ConditionalDialog>
      </DrawerWithPanel>
    )
  }

  override render() {
    return <>{this.renderDrawer(this.props)}</>
  }
}

const ConditionalDialog = (props: any) => {
  if (props.promptUpload) {
    return (
      <>
        <AlertDialog
          agreeText="Discard Files"
          content={
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <StyledText>
                  Are you sure you want to leave without saving?
                </StyledText>
              </DialogContentText>
            </DialogContent>
          }
          disagreeText="Cancel"
          onAgree={props.changeTab}
          onClose={props.closeDialog}
          onDisagree={props.closeDialog}
          open={props.openSaveChangesConfirmDialog || props.promptUpload}
          title="Unsaved Files"
        />
      </>
    )
  } else {
    return (
      <>
        <AlertDialog
          agreeText="Leave"
          content={
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <StyledText>
                  {props.unsavedFiles ? (
                    'Are you sure you want to leave before uploading?'
                  ) : (
                    <>
                      <DialogText>
                        You have unsaved changes. Any changes you
                        <br />
                        made will not be saved.
                      </DialogText>
                    </>
                  )}
                </StyledText>
              </DialogContentText>
            </DialogContent>
          }
          disagreeText="Cancel"
          onAgree={props.closeDrawer}
          onClose={props.closeDialog}
          onDisagree={props.closeDialog}
          open={props.openSaveChangesConfirmDialog || props.promptUpload}
          title="Leave without saving?"
        />
      </>
    )
  }
}

export default compose(withRouter)(FormDrawer)
